import type { z } from "zod";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Calls the backend with the student's Firebase ID token attached (none for public routes like the
 * contact form) and checks the reply against `schema`. Every failure becomes an ApiError with a
 * student-readable message.
 */
export async function apiRequest<T>(
  method: Method,
  path: string,
  idToken: string | null,
  schema: z.ZodType<T>,
  body?: unknown,
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...extraHeaders,
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiError(0, "We couldn't reach the server. Check your internet and try again.");
  }

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(response.status, errorBody?.error ?? "Something went wrong. Please try again.");
  }

  const parsed = schema.safeParse(await response.json().catch(() => null));
  if (!parsed.success) {
    throw new ApiError(response.status, "We got an unexpected answer from the server. Please try again.");
  }
  return parsed.data;
}

export function apiGet<T>(path: string, idToken: string, schema: z.ZodType<T>): Promise<T> {
  return apiRequest("GET", path, idToken, schema);
}
